import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, Search, X, ChevronLeft, Globe2 } from "lucide-react";
import { api, fmtEur, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import CountryCombobox from "../../components/CountryCombobox";
import InlineNumberEditor from "../../components/InlineNumberEditor";
import { toast } from "sonner";
import ConfirmDialog from "../../components/ConfirmDialog";

/**
 * Full-width Countries admin.
 *  - Top view: table of every configured country with Name, ISO, % adj, #overrides.
 *  - Click a row → opens a per-country detail view with the % adjustment editable
 *    and a searchable full product / size list where the admin sets overrides inline.
 */
export default function Countries() {
  const [items, setItems] = useState([]);
  const [overrideCounts, setOverrideCounts] = useState({}); // country_id -> count
  const [selected, setSelected] = useState(null); // country doc when in detail view
  const [newCode, setNewCode] = useState("");
  const [newPct, setNewPct] = useState(0);
  const [confirmId, setConfirmId] = useState(null);

  const usedCodes = useMemo(() => items.map((c) => (c.code || "").toUpperCase()).filter(Boolean), [items]);

  const loadCountries = useCallback(async () => {
    const [c, ovs] = await Promise.all([
      api.get("/countries"),
      api.get("/country-overrides"),
    ]);
    setItems(c.data);
    const counts = {};
    for (const o of ovs.data) counts[o.country_id] = (counts[o.country_id] || 0) + 1;
    setOverrideCounts(counts);
    // If we're currently in a detail view, refresh its snapshot too (functional
    // setState avoids depending on `selected` and re-creating the callback).
    setSelected((cur) => (cur ? c.data.find((x) => x.id === cur.id) || cur : cur));
  }, []);

  useEffect(() => { loadCountries(); }, [loadCountries]);

  const addCountry = async (e) => {
    e.preventDefault();
    if (!newCode) { toast.error("Pick a country from the list"); return; }
    try {
      const iso = (await api.get("/iso-countries")).data;
      const match = iso.find((c) => c.code === newCode);
      await api.post("/countries", {
        name: match ? match.name : newCode,
        code: newCode,
        percent_adjustment: Number(newPct) || 0,
      });
      setNewCode(""); setNewPct(0);
      toast.success("Country added");
      loadCountries();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const removeCountry = async (id) => {
    try { await api.delete(`/countries/${id}`); toast.success("Deleted"); loadCountries(); }
    catch (err) { toast.error(getErrorMessage(err, "Delete failed")); }
    finally { setConfirmId(null); if (selected?.id === id) setSelected(null); }
  };

  if (selected) {
    return (
      <CountryDetail
        country={selected}
        onBack={() => setSelected(null)}
        onCountryPatched={loadCountries}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Countries"
        subtitle="Configure per-country percent adjustments and per-size override prices."
        testid="page-countries"
      />
      <div className="p-4 sm:p-6 max-w-5xl">
        <form
          onSubmit={addCountry}
          className="bg-white border border-[#E2E4E0] rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2 items-end mb-4"
          data-testid="country-add-form"
        >
          <div>
            <label className="text-xs text-[#5C665A]">Country</label>
            <CountryCombobox
              value={newCode}
              onChange={setNewCode}
              placeholder="Pick from ISO list…"
              testid="country-new"
              disabledCodes={usedCodes}
            />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">% adjustment</label>
            <Input
              data-testid="country-new-pct"
              type="number"
              step="0.1"
              value={newPct}
              onChange={(e) => setNewPct(e.target.value)}
              className="h-10"
            />
          </div>
          <Button
            data-testid="country-add-btn"
            type="submit"
            className="h-10 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
          >
            <Plus size={14} /> Add
          </Button>
        </form>

        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F8] text-[#5C665A]">
                <tr>
                  <th className="text-left px-4 py-2.5 db-h font-semibold">Country</th>
                  <th className="text-left px-3 py-2.5 db-h font-semibold w-20">ISO</th>
                  <th className="text-right px-3 py-2.5 db-h font-semibold w-32">% adj</th>
                  <th className="text-right px-3 py-2.5 db-h font-semibold w-32">Overrides</th>
                  <th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-10 text-center text-[#5C665A]" data-testid="countries-empty">
                      <Globe2 size={22} className="mx-auto mb-1 text-[#5C665A]" />
                      No countries yet. Add one above to price for a market.
                    </td>
                  </tr>
                )}
                {items.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-[#E2E4E0] db-row cursor-pointer hover:bg-[#F9F9F8]"
                    onClick={() => setSelected(c)}
                    data-testid={`country-row-${c.id}`}
                  >
                    <td className="px-4 py-2.5 font-medium" data-testid={`country-name-${c.id}`}>{c.name}</td>
                    <td className="px-3 py-2.5 db-mono text-xs text-[#5C665A]">{c.code || "—"}</td>
                    <PctCell
                      country={c}
                      onSaved={(updated) => setItems((prev) => prev.map((x) => x.id === updated.id ? updated : x))}
                    />
                    <td className="px-3 py-2.5 text-right db-mono text-[#5C665A]" data-testid={`country-overrides-count-${c.id}`}>
                      {overrideCounts[c.id] || 0}
                    </td>
                    <td className="px-2 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center px-1.5 h-7 rounded-md"
                        onClick={() => setConfirmId(c.id)}
                        title="Delete"
                        data-testid={`country-delete-${c.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-[#5C665A] mt-3">
          Percent adjustment applies to base prices. Override prices win over percent for the specific size.
        </p>
      </div>

      <ConfirmDialog
        open={!!confirmId}
        onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete country?"
        description="This removes the country and all its overrides."
        confirmLabel="Delete"
        destructive
        onConfirm={() => removeCountry(confirmId)}
        testid="country-confirm-delete"
      />
    </>
  );
}

/** Inline-editable percent adjustment cell in the countries table.
 *  Explicit commit only: type → ✓/✗ buttons appear. Enter saves,
 *  Escape/Blur cancels. Value clamped -100..+500. */
function PctCell({ country, onSaved }) {
  const save = async (raw) => {
    const num = Number(raw);
    if (raw === "" || Number.isNaN(num)) {
      toast.error("Enter a number (e.g. 12.5 or -5)");
      throw new Error("invalid");
    }
    if (num < -100 || num > 500) {
      toast.error("% adjustment must be between -100 and +500");
      throw new Error("out_of_range");
    }
    if (num === country.percent_adjustment) return;
    // Optimistic: propagate updated country up, revert on error.
    const optimistic = { ...country, percent_adjustment: num };
    onSaved(optimistic);
    try {
      const { data } = await api.put(`/countries/${country.id}`, {
        name: country.name,
        code: country.code,
        percent_adjustment: num,
      });
      onSaved(data);
      toast.success("Saved");
    } catch (err) {
      onSaved(country); // revert
      toast.error(getErrorMessage(err, "Update failed"));
      throw err;
    }
  };

  return (
    <td
      className="px-3 py-2.5 text-right db-mono"
      onClick={(e) => e.stopPropagation()}
      data-testid={`country-pct-cell-${country.id}`}
    >
      <InlineNumberEditor
        value={country.percent_adjustment ?? 0}
        onCommit={save}
        step="0.1"
        min={-100}
        max={500}
        inputClassName="db-inline w-20 text-right"
        testid={`country-pct-input-${country.id}`}
        ariaLabel="Percent adjustment"
      />
    </td>
  );
}

/** Per-country detail view: editable header + full override list. */
function CountryDetail({ country, onBack, onCountryPatched }) {
  const [pct, setPct] = useState(country.percent_adjustment ?? 0);
  const [products, setProducts] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const dirtyPct = Number(pct) !== country.percent_adjustment;

  const loadAll = useCallback(async () => {
    const [p, ov] = await Promise.all([
      api.get("/products"),
      api.get(`/country-overrides?country_id=${country.id}`),
    ]);
    setProducts(p.data);
    setOverrides(ov.data);
  }, [country.id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const overrideBy = useMemo(
    () => Object.fromEntries(overrides.map((o) => [o.size_variant_id, o])),
    [overrides],
  );

  const savePct = async () => {
    setSaving(true);
    try {
      await api.put(`/countries/${country.id}`, {
        name: country.name,
        code: country.code,
        percent_adjustment: Number(pct) || 0,
      });
      toast.success("Country updated");
      onCountryPatched();
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setSaving(false); }
  };

  const setOverride = async (variant, price) => {
    const num = Number(price);
    if (Number.isNaN(num)) return;
    try {
      const { data } = await api.post("/country-overrides", {
        size_variant_id: variant.id,
        country_id: country.id,
        price: num,
      });
      setOverrides((prev) => {
        const rest = prev.filter((o) => o.size_variant_id !== variant.id);
        return [...rest, data];
      });
      onCountryPatched(); // refresh outer count
      toast.success("Override saved");
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
  };

  const clearOverride = async (variant) => {
    const ov = overrideBy[variant.id];
    if (!ov) return;
    try {
      await api.delete(`/country-overrides/${ov.id}`);
      setOverrides((prev) => prev.filter((o) => o.id !== ov.id));
      onCountryPatched();
      toast.success("Override cleared");
    } catch { toast.error("Failed"); }
  };

  // Effective per-size price: override > percent-adjusted > base.
  const effectivePrice = (basePrice, variantId) => {
    const ov = overrideBy[variantId];
    if (ov) return { price: ov.price, source: "override" };
    const pctNum = Number(pct) || 0;
    if (pctNum === 0) return { price: basePrice, source: "base" };
    const adjusted = Math.round(basePrice * (1 + pctNum / 100) * 100) / 100;
    return { price: adjusted, source: "percent" };
  };

  const filteredProducts = useMemo(() => {
    if (!q.trim()) return products;
    const s = q.toLowerCase();
    return products.filter(
      (p) => p.name.toLowerCase().includes(s) ||
             (p.size_variants || []).some((v) => (v.sku || "").toLowerCase().includes(s)),
    );
  }, [products, q]);

  const totalOverrides = overrides.length;

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <span>{country.name}</span>
            <span className="db-mono text-sm text-[#5C665A]">{country.code}</span>
          </span>
        }
        subtitle={`Country pricing · ${totalOverrides} override${totalOverrides === 1 ? "" : "s"}`}
        testid="page-country-detail"
        actions={
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-[#E2E4E0] text-sm hover:bg-[#F9F9F8]"
            data-testid="country-detail-back"
          >
            <ChevronLeft size={14} /> Countries
          </button>
        }
      />
      <div className="p-4 sm:p-6 max-w-6xl">
        <div className="bg-white border border-[#E2E4E0] rounded-lg p-4 mb-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-[#5C665A]">Percent adjustment</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                step="0.1"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="h-10 w-32 text-right db-mono"
                data-testid="country-detail-pct"
              />
              <span className="db-mono text-[#5C665A]">%</span>
              <Button
                onClick={savePct}
                disabled={!dirtyPct || saving}
                className="h-10 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-40"
                data-testid="country-detail-pct-save"
              >
                <Save size={14} /> Save
              </Button>
            </div>
            <p className="text-xs text-[#5C665A] mt-1">
              Applied to base price for sizes without an explicit override.
            </p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-[#5C665A]">Overrides configured</div>
            <div className="text-2xl font-bold db-mono" data-testid="country-detail-override-count">{totalOverrides}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1 sm:flex-none sm:w-96">
            <Search size={14} className="absolute left-2 top-2.5 text-[#5C665A]" />
            <Input
              data-testid="country-detail-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products or SKUs…"
              className="pl-7 h-9 w-full"
            />
          </div>
          <div className="ml-auto text-xs text-[#5C665A]">
            {filteredProducts.reduce((sum, p) => sum + (p.size_variants?.length || 0), 0)} sizes
          </div>
        </div>

        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#F9F9F8] text-[#5C665A] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 db-h font-semibold">Product</th>
                  <th className="text-left px-3 py-2 db-h font-semibold w-32">SKU</th>
                  <th className="text-right px-3 py-2 db-h font-semibold w-24">Base</th>
                  <th className="text-right px-3 py-2 db-h font-semibold w-32">Effective</th>
                  <th className="text-right px-3 py-2 db-h font-semibold w-28">Override €</th>
                  <th className="w-10 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-[#5C665A]" data-testid="country-detail-empty">
                      No products match &ldquo;{q}&rdquo;.
                    </td>
                  </tr>
                )}
                {filteredProducts.flatMap((p) => (p.size_variants || []).map((v) => {
                  const eff = effectivePrice(v.base_price, v.id);
                  return (
                    <tr
                      key={v.id}
                      className="border-t border-[#E2E4E0] db-row"
                      data-testid={`override-row-${v.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-[#5C665A]">{v.size_option_id ? "" : ""}</div>
                      </td>
                      <td className="px-3 py-2 db-mono text-xs text-[#5C665A]">{v.sku}</td>
                      <td className="px-3 py-2 text-right db-mono">{fmtEur(v.base_price)}</td>
                      <td className="px-3 py-2 text-right db-mono">
                        <span data-testid={`override-effective-${v.id}`}>{fmtEur(eff.price)}</span>
                        <div className="text-[10px] uppercase text-[#5C665A]" data-testid={`override-source-${v.id}`}>
                          {eff.source}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          defaultValue={overrideBy[v.id]?.price ?? ""}
                          placeholder="—"
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val === "" && overrideBy[v.id]) clearOverride(v);
                            else if (val !== "" && Number(val) !== overrideBy[v.id]?.price) setOverride(v, val);
                          }}
                          className="db-inline w-24 text-right"
                          data-testid={`override-price-${v.id}`}
                          key={overrideBy[v.id]?.price ?? "none"}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        {overrideBy[v.id] && (
                          <button
                            className="text-[#5C665A] hover:text-[#C94A4A]"
                            onClick={() => clearOverride(v)}
                            data-testid={`override-clear-${v.id}`}
                            title="Clear override"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                }))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
